import * as React from "react";
import { DateTime } from "luxon";

import {
  IPublicProcessedGame,
  IPublicProcessedShift,
  IPublicSupervisor,
} from "../../../types/shifts/PublicShift";
import { toDateTime } from "../../../util/date+helper";
import { prepareColorForDarkMode } from "../../../util/string";

import "./PublicRosterModern.css";

const TICK_HEIGHT_PX = 36;

interface IPublicRosterModernProps {
  shift: IPublicProcessedShift;
  activeVenueKey: string;
  onVenueChange: (venueKey: string) => void;
}

type SlotRow = {
  slot?: unknown;
  [key: string]: unknown;
};

const PHONE_REGEX =
  /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

const getContrastColor = (hex?: string) => {
  if (!hex) {
    return "#ffffff";
  }
  let color = hex.trim();
  if (color.startsWith("#")) {
    color = color.slice(1);
  }
  if (color.length === 3) {
    color = color
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  if (color.length !== 6) {
    return "#ffffff";
  }
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.55 ? "#10121a" : "#ffffff";
};

const formatGameRange = (game: IPublicProcessedGame) => {
  const start = game.date;
  if (!DateTime.isDateTime(start) || !start.isValid) {
    return "";
  }

  const finish = start.plus({ minutes: game.duration ?? 0 });
  return `${start.toFormat("h:mm a")} - ${finish.toFormat("h:mm a")}`;
};

const formatSupervisorTime = (supervisor: IPublicSupervisor) => {
  const start = toDateTime(supervisor.start);
  const finish = toDateTime(supervisor.finish);

  if (!start || !finish) {
    return "Time TBC";
  }

  return `${start.toFormat("h:mm a")} - ${finish.toFormat("h:mm a")}`;
};

const PublicRosterModern: React.FC<IPublicRosterModernProps> = ({
  shift,
  activeVenueKey,
  onVenueChange,
}) => {
  const venue = shift.venues[activeVenueKey];
  const venueName = venue?.name ?? activeVenueKey;
  const courts = venue?.courts ?? [];
  const supervisors = shift.supervisors?.[activeVenueKey] ?? [];

  const venueSlots = React.useMemo(
    () => (shift.slots[activeVenueKey] ?? []) as SlotRow[],
    [shift.slots, activeVenueKey],
  );

  const venuesList = React.useMemo(
    () => Object.entries(shift.venues),
    [shift.venues],
  );

  const now = React.useMemo(() => DateTime.now(), []);

  const timezoneLabel = React.useMemo(() => {
    if (!venue?.tz) {
      return null;
    }
    const zoned = DateTime.now().setZone(venue.tz);
    if (!zoned.isValid) {
      return venue.tz;
    }
    const shortName = zoned.toFormat("ZZZ");
    return shortName || venue.tz;
  }, [venue?.tz]);

  const tickMinutes = shift.tickDuration || 5;

  const hasGames = React.useMemo(
    () =>
      venueSlots.some((slotRow) =>
        courts.some((court) => {
          const value = slotRow[court];
          return value && typeof value === "object";
        }),
      ),
    [venueSlots, courts],
  );

  const earliestGameStart = React.useMemo(() => {
    let earliest: DateTime | null = null;
    venueSlots.forEach((slotRow) => {
      courts.forEach((court) => {
        const rawValue = slotRow[court];
        if (rawValue && typeof rawValue === "object") {
          const game = rawValue as IPublicProcessedGame;
          if (DateTime.isDateTime(game.date) && game.date.isValid) {
            if (!earliest || game.date < earliest) {
              earliest = game.date;
            }
          }
        }
      });
    });
    return earliest;
  }, [venueSlots, courts]);

  const baseTime = React.useMemo(() => {
    if (earliestGameStart) {
      return earliestGameStart;
    }
    if (DateTime.isDateTime(shift.start) && shift.start.isValid) {
      return shift.start;
    }
    return DateTime.now();
  }, [earliestGameStart, shift.start]);

  const TIME_COLUMN_WIDTH = 104;
  const CARD_MIN_WIDTH = 190;
  const tickHeight = 30;

  const timeLabels = React.useMemo(
    () =>
      venueSlots.map((_, idx) =>
        baseTime.plus({ minutes: idx * tickMinutes }),
      ),
    [venueSlots, baseTime, tickMinutes],
  );

  const columnTemplate = React.useMemo(() => {
    const safeCount = Math.max(courts.length, 1);
    return `${TIME_COLUMN_WIDTH}px repeat(${safeCount}, minmax(${CARD_MIN_WIDTH}px, 1fr))`;
  }, [courts.length]);

  const scheduleHeaderStyle = React.useMemo(() => {
    const style: React.CSSProperties = {
      gridTemplateColumns: columnTemplate,
    };
    (style as Record<string, string | number>)["--time-column-width"] = `${TIME_COLUMN_WIDTH}px`;
    return style;
  }, [columnTemplate]);

  const scheduleBodyStyle = React.useMemo(() => {
    const style: React.CSSProperties = {
      gridTemplateColumns: columnTemplate,
    };
    const custom = style as Record<string, string | number>;
    custom["--tick-height"] = `${tickHeight}px`;
    custom["--court-count"] = `${Math.max(courts.length, 1)}`;
    custom["--time-column-width"] = `${TIME_COLUMN_WIDTH}px`;
    return style;
  }, [columnTemplate, courts.length]);

  const trackHeight = Math.max(venueSlots.length, 1) * tickHeight;

  const labelEvery = React.useMemo(() => {
    if (tickMinutes >= 30) {
      return 1;
    }
    if (tickMinutes >= 20) {
      return 1;
    }
    if (tickMinutes >= 15) {
      return 2;
    }
    if (tickMinutes >= 10) {
      return 2;
    }
    return 3;
  }, [tickMinutes]);

  const timesShownLabel = timezoneLabel
    ? `Times shown in ${timezoneLabel}`
    : "Times shown in local time";

  return (
    <div className="public-roster-modern">
      <div className="public-roster-modern__surface">
        <div className="public-roster-modern__top-line">
          <span>{now.toFormat("h:mm a")}</span>
          <span>{now.toFormat("ccc d LLL")}</span>
        </div>

        <div className="public-roster-modern__meta-wrap">
          <div className="public-roster-modern__headline">
            <div className="public-roster-modern__headline-text">
              <span className="public-roster-modern__eyebrow">
                Published Roster
              </span>
              <h2 className="public-roster-modern__title">{shift.name}</h2>
            </div>
            <p className="public-roster-modern__subtitle">
              {shift.start.toFormat("cccc, d MMMM yyyy")}
            </p>
          </div>
          <div className="public-roster-modern__meta">
            <span className="public-roster-modern__meta-label">
              Selected venue
            </span>
            <span className="public-roster-modern__meta-value">
              {venueName}
            </span>
          </div>
        </div>

        <div className="public-roster-modern__venues">
          {venuesList.map(([key, value]) => {
            const isActive = key === activeVenueKey;
            const displayName = value.name ?? key;
            return (
              <button
                type="button"
                key={key}
                onClick={() => onVenueChange(key)}
                className={`public-roster-modern__venue${
                  isActive ? " public-roster-modern__venue--active" : ""
                }`}
                aria-pressed={isActive}
              >
                {displayName}
              </button>
            );
          })}
        </div>

        <div className="public-roster-modern__panel">
          <div className="public-roster-modern__panel-heading">
            <h3 className="public-roster-modern__panel-title">Supervisors</h3>
          </div>
          {supervisors.length === 0 ? (
            <p className="public-roster-modern__muted">
              Supervisors will appear here once they have been published for
              this venue.
            </p>
          ) : (
            <div className="public-roster-modern__supervisor-grid">
              {supervisors.map((supervisor) => (
                <div
                  key={`${supervisor.name}-${supervisor.start}-${supervisor.finish}`}
                  className="public-roster-modern__supervisor-card"
                >
                  <span className="public-roster-modern__supervisor-name">
                    {supervisor.name}
                  </span>
                  <span className="public-roster-modern__supervisor-time">
                    {formatSupervisorTime(supervisor)}
                  </span>
                  <span className="public-roster-modern__supervisor-contact">
                    {supervisor.mobile ? (
                      PHONE_REGEX.test(supervisor.mobile) ? (
                        <a href={`tel:${supervisor.mobile}`}>
                          {supervisor.mobile}
                        </a>
                      ) : (
                        supervisor.mobile
                      )
                    ) : (
                      "Login to see supervisor details"
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="public-roster-modern__panel public-roster-modern__panel--schedule">
          <div className="public-roster-modern__panel-heading">
            <div>
              <h3 className="public-roster-modern__panel-title">Schedule</h3>
              <p className="public-roster-modern__muted">
                Upcoming games for {venueName}
              </p>
            </div>
            <span className="public-roster-modern__muted">
              {timesShownLabel}
            </span>
          </div>

          {courts.length === 0 ? (
            <p className="public-roster-modern__muted">
              No courts configured for this venue.
            </p>
          ) : !hasGames || venueSlots.length === 0 ? (
            <p className="public-roster-modern__muted">
              No games are scheduled for this venue yet.
            </p>
          ) : (
            <div className="public-roster-modern__schedule">
              <div className="public-roster-modern__schedule-track">
                <div
                  className="public-roster-modern__schedule-header"
                  style={scheduleHeaderStyle}
                >
                  <div className="public-roster-modern__schedule-heading public-roster-modern__schedule-heading--time">
                    Time
                  </div>
                  {courts.map((court) => (
                    <div
                      key={`header-${court}`}
                      className="public-roster-modern__schedule-heading"
                    >
                      {court}
                    </div>
                  ))}
                </div>
                <div
                  className="public-roster-modern__schedule-body"
                  style={scheduleBodyStyle}
                >
                  <div
                    className="public-roster-modern__time-column"
                    style={{ height: `${trackHeight}px` }}
                  >
                    {timeLabels.map((label, idx) => (
                      <div
                        key={`time-${label.toMillis()}-${idx}`}
                        className="public-roster-modern__time-slot"
                      >
                        {labelEvery === 1 || idx % labelEvery === 0
                          ? label.toFormat("h:mm a")
                          : ""}
                      </div>
                    ))}
                  </div>

                  {courts.map((court) => (
                    <div
                      key={`court-${court}`}
                      className="public-roster-modern__court-column"
                    >
                      <div
                        className="public-roster-modern__court-track"
                        style={{ height: `${trackHeight}px` }}
                      >
                        {venueSlots.map((slotRow, rowIdx) => {
                          const rawValue = slotRow[court];
                          if (!rawValue || typeof rawValue !== "object") {
                            return null;
                          }

                          const game = rawValue as IPublicProcessedGame;
                          const blockTicks = Math.max(1, game.ticks || 1);
                          const startMillis =
                            DateTime.isDateTime(game.date) && game.date.isValid
                              ? game.date.toMillis()
                              : rowIdx;
                          return (
                            <div
                              key={`${court}-${startMillis}-${rowIdx}`}
                              className="public-roster-modern__game-block"
                              style={{
                                top: `calc(${rowIdx} * var(--tick-height))`,
                                height: `calc(${blockTicks} * var(--tick-height))`,
                              }}
                            >
                              <div className="public-roster-modern__game-card public-roster-modern__game-card--floating">
                                <div className="public-roster-modern__game-time">
                                  {formatGameRange(game)}
                                </div>
                                <div className="public-roster-modern__game-competition">
                                  {game.competition}
                                </div>
                                <div className="public-roster-modern__game-teams">
                                  {game.teams}
                                </div>
                                {game.labels && game.labels.length > 0 && (
                                  <div className="public-roster-modern__game-tags">
                                    {game.labels.map((label) => {
                                      const backgroundColor = prepareColorForDarkMode(
                                        label.color || "#274874",
                                      );
                                      const textColor =
                                        getContrastColor(backgroundColor);
                                      return (
                                        <span
                                          key={`${label.name}-${label.color}`}
                                          className="public-roster-modern__tag"
                                          style={{
                                            backgroundColor,
                                            color: textColor,
                                          }}
                                        >
                                          {label.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {game.officials && game.officials.length > 0 && (
                                  <div className="public-roster-modern__officials">
                                    {game.officials.map((official) => {
                                      const backgroundColor = prepareColorForDarkMode(
                                        official.color || "#2a3d63",
                                      );
                                      const textColor =
                                        getContrastColor(backgroundColor);
                                      return (
                                        <span
                                          key={official.name}
                                          className="public-roster-modern__official"
                                          style={{
                                            backgroundColor,
                                            color: textColor,
                                          }}
                                        >
                                          {official.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicRosterModern;
